import { ExternalLinkIcon } from '@heroicons/react/outline';
import { Card, Title } from '@yoast/ui-library';
import React from 'react';
import Divider from '../../components/Divider';
import useScan from '../../hooks/useScan';

const NoResultsCard: React.FC = () => {
  const { data: scan, isLoading, isRefetching } = useScan();
  if (
    (isLoading && !isRefetching) ||
    scan?.mentions?.mentionedInNumberOfQueries?.value !== 0
  ) {
    return null;
  }

  const steps = [
    {
      title: 'Activate llms.txt',
      description:
        'Make sure AI systems know where to look and which pages matter most.',
      link: {
        url: 'https://yoast.com/help/enable-llmstxt/',
        text: 'Set up llms.txt in Yoast SEO',
      },
    },
    {
      title: 'Adopt an LLM-friendly content structure',
      description:
        'Start with clear definitions or TL;DRs, use subheadings, and format answers so LLMs can lift them directly.',
      link: {
        url: 'https://yoast.com/yoast-seo-premium-september-9-2025/',
        text: 'Learn how to generate AI summaries',
      },
    },
    {
      title: 'Simplify writing and readability',
      description:
        'Use shorter sentences, plain language, and one idea per paragraph. This makes it easier for AI systems to recognize and quote your content.',
    },
  ];

  return (
    <Card className="yst-overflow-visible yst-p-8 yst-text-slate-800 md:yst-col-span-2">
      <Card.Content className="yst-max-w-2xl">
        <Title size="2" className="yst-mb-2 yst-text-slate-900">
          We couldn't find your brand in this brand analysis...
        </Title>
        <p className="yst-text-slate-600">
          This means LLMs may not yet be surfacing your site in answers. Don't
          worry, here are some steps you can take today to boost your AI
          visibility:
        </p>
        <ol className="yst-my-0 yst-list-none yst-divide-y yst-divide-slate-200 [counter-reset:list-counter]">
          {steps.map((step, index) => (
            <li
              key={index}
              className="yst-relative yst-py-4 [counter-increment:list-counter] before:yst-absolute before:yst-left-0 before:yst-font-semibold before:yst-text-slate-800 before:yst-content-[counter(list-counter)'.'] last:yst-pb-0"
            >
              <div className="yst-ml-4 yst-text-slate-600">
                <strong className="yst-font-medium yst-text-slate-800">
                  {step.title}
                </strong>
                <br />
                {step.description}
                {step.link && (
                  <>
                    <br />
                    <span className="yst-mt-2 yst-block">
                      <a
                        href={step.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="yst-font-medium"
                      >
                        {step.link.text}
                        <ExternalLinkIcon className="yst-ml-1 yst-inline yst-h-3 yst-w-3" />
                      </a>
                    </span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>

        <Divider size={4} className="yst-pt-0" />

        <p className="yst-text-slate-600">
          Want to go deeper? →{' '}
          <a
            href="https://yoast.com/how-to-optimize-content-for-llms/"
            target="_blank"
            rel="noopener noreferrer"
            className="yst-font-medium"
          >
            Read our full guide on optimizing content for LLMs
            <ExternalLinkIcon className="yst-ml-1 yst-inline yst-h-3 yst-w-3" />
          </a>
        </p>
      </Card.Content>
    </Card>
  );
};

export default NoResultsCard;
